import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMusicBrainz } from '../../hooks/useMusicBrainz';

function SearchBar({
  onSearch,
  activeArtist,
  disabled = false,
  placeholder = 'Search an artist or group',
  onSeeking,
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [focused, setFocused] = useState(false);
  const { searchArtistLight } = useMusicBrainz();
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const listId = 'search-suggestion-list';
  const activeArtistId = activeArtist?.id;

  const collapse = useCallback(() => {
    if (!activeArtistId) return;
    setIsExpanded(false);
    setSearchTerm('');
    setSuggestions([]);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  }, [activeArtistId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        if (isExpanded && !searchTerm && activeArtistId) {
          collapse();
        } else {
          setShowSuggestions(false);
        }
      }
    };

    const handleEscKey = (event) => {
      if (event.key !== 'Escape') return;
      if (showSuggestions) {
        setShowSuggestions(false);
        return;
      }
      if (isExpanded && activeArtistId) {
        collapse();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isExpanded, searchTerm, activeArtistId, showSuggestions, collapse]);

  useEffect(() => {
    if (activeArtistId) {
      setIsExpanded(false);
      setSearchTerm('');
      setSuggestions([]);
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    } else {
      setIsExpanded(true);
    }
  }, [activeArtistId]);

  useEffect(() => {
    if (isExpanded && activeArtistId && !disabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded, activeArtistId, disabled]);

  useEffect(() => {
    onSeeking?.(Boolean(focused || showSuggestions));
  }, [focused, showSuggestions, onSeeking]);

  useEffect(() => {
    if (disabled) {
      setSuggestions([]);
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      if (searchTerm.length >= 3) {
        try {
          const results = await searchArtistLight(searchTerm, 4);
          const next = results || [];
          setSuggestions(next);
          setShowSuggestions(true);
          setHighlightedIndex(next.length ? 0 : -1);
        } catch (error) {
          if (error?.name === 'AbortError') return;
          console.error('Error fetching suggestions:', error);
          setSuggestions([]);
          setShowSuggestions(true);
          setHighlightedIndex(-1);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, searchArtistLight, disabled]);

  const selectSuggestion = (suggestion) => {
    onSearch(suggestion.id, suggestion.name);
    setSearchTerm('');
    setShowSuggestions(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
    if (activeArtistId) {
      setIsExpanded(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (disabled) return;
    const pick = suggestions[highlightedIndex] || suggestions[0];
    if (pick) {
      selectSuggestion(pick);
    }
  };

  const handleInputFocus = () => {
    setFocused(true);
    if (suggestions.length > 0 || searchTerm.length >= 3) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    window.setTimeout(() => {
      if (searchRef.current?.contains(document.activeElement)) return;
      setFocused(false);
    }, 120);
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'Escape' && showSuggestions) {
      event.preventDefault();
      setShowSuggestions(false);
      return;
    }

    if (!showSuggestions || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((index) => (
        index < suggestions.length - 1 ? index + 1 : 0
      ));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((index) => (
        index > 0 ? index - 1 : suggestions.length - 1
      ));
    }
  };

  const canSubmit = !disabled && suggestions.length > 0;
  const suggestionsOpen = showSuggestions && (suggestions.length > 0 || searchTerm.length >= 3);
  const activeSuggestionId = highlightedIndex >= 0 && suggestions[highlightedIndex]
    ? `search-suggestion-${suggestions[highlightedIndex].id}`
    : undefined;

  return (
    <div className={`search-bar-container ${isExpanded ? 'is-expanded' : ''} ${suggestionsOpen ? 'suggestions-open' : ''}`} ref={searchRef}>
      {isExpanded ? (
        <>
          <form className="search-bar" onSubmit={handleSubmit} role="search">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              placeholder={placeholder}
              className="search-input"
              disabled={disabled}
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={suggestionsOpen}
              aria-controls={listId}
              aria-activedescendant={activeSuggestionId}
            />
            {canSubmit ? (
              <button
                type="submit"
                className="search-button has-results"
                aria-label="Search"
              >
                <span className="music-icon">♪</span>
              </button>
            ) : activeArtistId ? (
              <button
                type="button"
                className="search-button search-button-close"
                onClick={collapse}
                aria-label="Close search"
              >
                <span aria-hidden="true">×</span>
              </button>
            ) : (
              <button
                type="submit"
                disabled
                className="search-button"
                aria-label="Search"
              >
                <span className="music-icon">♪</span>
              </button>
            )}
          </form>
          {showSuggestions && (
            <ul className="search-suggestions" id={listId} role="listbox">
              {suggestions.length > 0 ? (
                suggestions.map((suggestion, index) => (
                  <li
                    key={suggestion.id}
                    id={`search-suggestion-${suggestion.id}`}
                    role="option"
                    aria-selected={index === highlightedIndex}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      selectSuggestion(suggestion);
                    }}
                    className={`suggestion-item${index === highlightedIndex ? ' is-active' : ''}${index === 0 ? ' is-default' : ''}`}
                  >
                    <span className="suggestion-name">{suggestion.name}</span>
                    {suggestion.disambiguation && (
                      <span className="disambiguation">{suggestion.disambiguation}</span>
                    )}
                  </li>
                ))
              ) : (
                searchTerm.length >= 3 && (
                  <li className="search-helper-text" role="presentation">
                    No matches found
                  </li>
                )
              )}
            </ul>
          )}
        </>
      ) : (
        <button
          type="button"
          className="search-bar-collapsed"
          onClick={() => setIsExpanded(true)}
          aria-label="Seek artist or group"
        >
          Seek
        </button>
      )}
    </div>
  );
}

export default SearchBar;
